/**
 * §12.2 — Voice management, and §12.5 — dynamic mixing (ducking).
 *
 * `VoiceAllocator` is deliberately free of WebAudio: it is pure integer/float bookkeeping
 * over preallocated slots, so the §12.2 stealing rules are unit-testable without an
 * AudioContext. The WebAudio side (fading a stolen voice) lives in `sfx.ts`.
 *
 * Spine §13 / AMD-4: 32 total voices, 3 reserved for music, priority classes
 * warnings (5) > player_damage (4) > boss (3) > weapons/enemy/pickups/levelup/ui (2) > ambient (1).
 */

import type { FamilyName } from './context';
import { FAMILY_NAMES, rampTo, type AudioGraph } from './context';

export const VOICE_CAP = 32;
export const MUSIC_RESERVED = 3;
export const SFX_VOICE_CAP = VOICE_CAP - MUSIC_RESERVED; // 29

/** §12.2 priority classes. Music is unclassified: never steals, never stolen. */
export const PRIORITY: Readonly<Record<FamilyName, number>> = {
  warnings: 5,
  player_damage: 4,
  boss: 3,
  weapons: 2,
  enemy: 2,
  pickups: 2,
  levelup: 2,
  ui: 2,
  ambient: 1,
  music: 0,
};

/** §12.2 per-family sub-caps. The nine SFX families sum to exactly 29. */
export const FAMILY_SUB_CAP: Readonly<Record<FamilyName, number>> = {
  weapons: 11,
  enemy: 5,
  boss: 3,
  warnings: 2,
  player_damage: 2,
  pickups: 2,
  levelup: 1,
  ui: 2,
  ambient: 1,
  music: MUSIC_RESERVED,
};

/** §12.2 rule (4) — a voice inside its attack transient is unstealable. */
export const ATTACK_LOCK_S = 0.03;
/** §12.2 rule (5) — steal fade, never a hard cut. */
export const STEAL_FADE_S = 0.02;

export const DENIED = -1;

export interface VoiceSlot {
  active: boolean;
  /** Monotonic id for telemetry; 0 when free. */
  voiceId: number;
  cueId: string;
  family: FamilyName;
  priority: number;
  /** Requested peak gain — the tie-break key inside a class (§12.2 rule 2). */
  gain: number;
  startAt: number;
  endAt: number;
  /** Ascending allocation counter; the oldest voice has the smallest value. */
  seq: number;
  loop: boolean;
}

export interface AllocResult {
  /** Slot index, or DENIED. */
  index: number;
  /** Slot that was faded to make room, or DENIED. */
  stolen: number;
  /** Voice id of the stolen voice, for the steal log. */
  stolenVoiceId: number;
}

const STEAL_LOG_CAP = 256;

export class VoiceAllocator {
  readonly slots: VoiceSlot[] = [];
  private seqCounter = 1;
  private voiceIdCounter = 1;
  private liveCount = 0;
  private readonly familyLive: Record<FamilyName, number>;
  private readonly result: AllocResult = { index: DENIED, stolen: DENIED, stolenVoiceId: 0 };

  /** Ring buffer of steal events: (stolen_id, thief_id, tick) triples (§12.2 acceptance). */
  readonly stealLog = new Int32Array(STEAL_LOG_CAP * 3);
  stealLogWrite = 0;
  stealCount = 0;
  /** Peak concurrent SFX voices seen — telemetry asserts ≤ 29 (+3 music = 32). */
  peakLive = 0;
  deniedCount = 0;

  constructor(capacity: number = SFX_VOICE_CAP) {
    for (let i = 0; i < capacity; i++) {
      this.slots.push({
        active: false,
        voiceId: 0,
        cueId: '',
        family: 'ui',
        priority: 0,
        gain: 0,
        startAt: 0,
        endAt: 0,
        seq: 0,
        loop: false,
      });
    }
    const fam = {} as Record<FamilyName, number>;
    for (let i = 0; i < FAMILY_NAMES.length; i++) fam[FAMILY_NAMES[i]] = 0;
    this.familyLive = fam;
  }

  get live(): number {
    return this.liveCount;
  }

  familyCount(f: FamilyName): number {
    return this.familyLive[f];
  }

  /** Frees every slot whose scheduled end has passed. Cheap: single ascending pass. */
  prune(now: number): void {
    const slots = this.slots;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (s.active && !s.loop && s.endAt <= now) this.freeSlot(i);
    }
  }

  private freeSlot(i: number): void {
    const s = this.slots[i];
    if (!s.active) return;
    s.active = false;
    this.liveCount--;
    this.familyLive[s.family]--;
    s.voiceId = 0;
    s.cueId = '';
    s.loop = false;
  }

  /** Explicit release, used when a loop stops or a WebAudio voice reports it ended. */
  release(index: number, voiceId: number): void {
    if (index < 0 || index >= this.slots.length) return;
    const s = this.slots[index];
    if (!s.active || s.voiceId !== voiceId) return;
    this.freeSlot(index);
  }

  /**
   * §12.2 allocation. Returns the slot to use plus the slot that had to be faded.
   * Order of checks: prune → free slot → family sub-cap steal → global priority steal.
   */
  request(
    now: number,
    cueId: string,
    family: FamilyName,
    gain: number,
    endAt: number,
    loop: boolean,
    tick: number,
  ): AllocResult {
    this.prune(now);
    const res = this.result;
    res.index = DENIED;
    res.stolen = DENIED;
    res.stolenVoiceId = 0;
    const priority = PRIORITY[family];

    // (1) Family sub-cap. Over cap, a cue can only take a voice from its own family.
    if (this.familyLive[family] >= FAMILY_SUB_CAP[family]) {
      const victim = this.pickVictimInFamily(now, family, priority >= 5);
      if (victim === DENIED) {
        this.deniedCount++;
        return res;
      }
      res.stolen = victim;
      res.stolenVoiceId = this.slots[victim].voiceId;
      this.logSteal(res.stolenVoiceId, tick);
      this.freeSlot(victim);
    } else if (this.liveCount >= this.slots.length) {
      // (2) Global cap. Steal from the lowest priority class strictly below the requester.
      const victim = this.pickVictimGlobal(now, priority);
      if (victim === DENIED) {
        this.deniedCount++;
        return res;
      }
      res.stolen = victim;
      res.stolenVoiceId = this.slots[victim].voiceId;
      this.logSteal(res.stolenVoiceId, tick);
      this.freeSlot(victim);
    }

    const idx = this.findFree();
    if (idx === DENIED) {
      this.deniedCount++;
      return res;
    }
    const s = this.slots[idx];
    s.active = true;
    s.voiceId = this.voiceIdCounter++;
    s.cueId = cueId;
    s.family = family;
    s.priority = priority;
    s.gain = gain;
    s.startAt = now;
    s.endAt = endAt;
    s.seq = this.seqCounter++;
    s.loop = loop;
    this.liveCount++;
    this.familyLive[family]++;
    if (this.liveCount > this.peakLive) this.peakLive = this.liveCount;
    res.index = idx;
    if (res.stolen !== DENIED) this.setThief(s.voiceId);
    return res;
  }

  private findFree(): number {
    const slots = this.slots;
    for (let i = 0; i < slots.length; i++) if (!slots[i].active) return i;
    return DENIED;
  }

  /**
   * §12.2 rule (2): within a class steal the lowest-gain voice, ties → oldest.
   * Rule (4): skip voices still inside their 30 ms attack, unless the requester is a
   * warning (rule 3 — warnings never yield and a 3rd warning takes the older one).
   */
  private pickVictimInFamily(now: number, family: FamilyName, ignoreAttackLock: boolean): number {
    const slots = this.slots;
    let best = DENIED;
    let bestGain = Infinity;
    let bestSeq = Infinity;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s.active || s.family !== family) continue;
      if (!ignoreAttackLock && now - s.startAt < ATTACK_LOCK_S) continue;
      if (s.gain < bestGain - 1e-6 || (Math.abs(s.gain - bestGain) <= 1e-6 && s.seq < bestSeq)) {
        best = i;
        bestGain = s.gain;
        bestSeq = s.seq;
      }
    }
    return best;
  }

  /** Steals from the lowest priority class that has a freeable voice below the requester. */
  private pickVictimGlobal(now: number, priority: number): number {
    const slots = this.slots;
    let best = DENIED;
    let bestPriority = Infinity;
    let bestGain = Infinity;
    let bestSeq = Infinity;
    for (let i = 0; i < slots.length; i++) {
      const s = slots[i];
      if (!s.active) continue;
      if (s.priority >= priority) continue; // never steal from an equal or higher class
      if (now - s.startAt < ATTACK_LOCK_S) continue;
      if (
        s.priority < bestPriority ||
        (s.priority === bestPriority &&
          (s.gain < bestGain - 1e-6 || (Math.abs(s.gain - bestGain) <= 1e-6 && s.seq < bestSeq)))
      ) {
        best = i;
        bestPriority = s.priority;
        bestGain = s.gain;
        bestSeq = s.seq;
      }
    }
    return best;
  }

  private logSteal(stolenVoiceId: number, tick: number): void {
    const base = this.stealLogWrite * 3;
    this.stealLog[base] = stolenVoiceId;
    this.stealLog[base + 1] = 0; // thief filled in by setThief
    this.stealLog[base + 2] = tick;
    this.stealCount++;
  }

  private setThief(thiefVoiceId: number): void {
    this.stealLog[this.stealLogWrite * 3 + 1] = thiefVoiceId;
    this.stealLogWrite = (this.stealLogWrite + 1) % STEAL_LOG_CAP;
  }

  reset(): void {
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      s.active = false;
      s.voiceId = 0;
      s.cueId = '';
      s.loop = false;
    }
    for (let i = 0; i < FAMILY_NAMES.length; i++) this.familyLive[FAMILY_NAMES[i]] = 0;
    this.liveCount = 0;
    this.peakLive = 0;
    this.stealCount = 0;
    this.deniedCount = 0;
    this.stealLogWrite = 0;
  }
}

// ---------------------------------------------------------------------------
// §12.5 sidechain ducking
// ---------------------------------------------------------------------------

/** Duck sources that target the weapons bus, with their §12.5 depths in dB. */
export const DUCK_SOURCES = ['warnings', 'boss', 'player_damage', 'recovery', 'density'] as const;
export type DuckSource = (typeof DUCK_SOURCES)[number];

export const DUCK_DEPTH_DB: Readonly<Record<DuckSource, number>> = {
  warnings: -6,
  boss: -3,
  player_damage: -4,
  recovery: -3,
  density: -6,
};

export const DUCK_ATTACK_S = 0.005;
export const DUCK_RELEASE_S = 0.3;

export function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

export function linearToDb(x: number): number {
  return 20 * Math.log10(Math.max(1e-6, x));
}

/**
 * Applies the deepest active duck to a target bus. Purely additive in time: a deeper or
 * longer duck extends the automation, a shallower one is ignored while a deeper one holds.
 */
export class Ducker {
  private until: Record<DuckSource, number>;
  private currentDb = 0;
  private currentUntil = 0;
  private baseGain: number;

  constructor(baseGain: number) {
    this.baseGain = baseGain;
    const u = {} as Record<DuckSource, number>;
    for (let i = 0; i < DUCK_SOURCES.length; i++) u[DUCK_SOURCES[i]] = 0;
    this.until = u;
  }

  setBaseGain(v: number): void {
    this.baseGain = v;
  }

  /** Pure part: records the request and returns the resulting target gain, or −1 for "no change". */
  request(source: DuckSource, now: number, durationS: number): number {
    const end = now + durationS;
    if (end > this.until[source]) this.until[source] = end;
    let db = 0;
    let until = 0;
    for (let i = 0; i < DUCK_SOURCES.length; i++) {
      const s = DUCK_SOURCES[i];
      if (this.until[s] > now) {
        if (DUCK_DEPTH_DB[s] < db) db = DUCK_DEPTH_DB[s];
        if (this.until[s] > until) until = this.until[s];
      }
    }
    if (db === this.currentDb && until <= this.currentUntil) return -1;
    this.currentDb = db;
    this.currentUntil = until;
    return this.baseGain * dbToLinear(db);
  }

  /** Current duck depth in dB (≤ 0). Telemetry reads this for the §12.6 assertion. */
  get depthDb(): number {
    return this.currentDb;
  }

  get releaseAt(): number {
    return this.currentUntil;
  }

  /** WebAudio side: schedule attack now and release at the known expiry. */
  apply(g: AudioGraph, param: AudioParam, source: DuckSource, now: number, durationS: number): void {
    const target = this.request(source, now, durationS);
    if (target < 0) return;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(target, now + DUCK_ATTACK_S);
    if (this.currentUntil > now) {
      param.setValueAtTime(target, this.currentUntil);
      param.linearRampToValueAtTime(this.baseGain, this.currentUntil + DUCK_RELEASE_S);
    }
    void g;
  }

  reset(g: AudioGraph | null, param: AudioParam | null): void {
    for (let i = 0; i < DUCK_SOURCES.length; i++) this.until[DUCK_SOURCES[i]] = 0;
    this.currentDb = 0;
    this.currentUntil = 0;
    if (g && param) rampTo(param, this.baseGain, g.ctx.currentTime);
  }
}

/** §12.5 music layer gains for a director intensity I, equal-power normalized. */
export function layerGains(I: number, out: Float32Array): Float32Array {
  const i = I < 0 ? 0 : I > 1 ? 1 : I;
  const l1 = 1 - i;
  const l2 = 4 * i * (1 - i);
  const l3 = i;
  const power = Math.sqrt(l1 * l1 + l2 * l2 + l3 * l3);
  const k = power > 1e-6 ? 1 / power : 0;
  out[0] = l1 * k;
  out[1] = l2 * k;
  out[2] = l3 * k;
  return out;
}
